import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { ArrowLeft, ArrowRight, Send, MessageCircle, Check, Compass, ListChecks, CalendarClock, Mailbox, PartyPopper } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";

const STEP_ICONS = [Compass, ListChecks, CalendarClock, Mailbox];

const celebrate = () => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const colors = ["#E9B44C", "#63744F", "#E8813A", "#3E7E8C", "#8F6115"];
  confetti({ particleCount: 90, spread: 80, origin: { y: 0.65 }, colors });
  setTimeout(() => confetti({ particleCount: 60, spread: 110, origin: { y: 0.55 }, colors }), 250);
};

const LeadQuiz = () => {
  const { t, lang } = useLanguage();
  const q = t.quiz;
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [answers, setAnswers] = useState({ situation: "", needs: [], timeline: "", name: "", contact: "", message: "" });
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (status === "sent") celebrate();
  }, [status]);

  const toggleNeed = (n) =>
    setAnswers((a) => ({
      ...a,
      needs: a.needs.includes(n) ? a.needs.filter((x) => x !== n) : [...a.needs, n],
    }));

  const canNext =
    step === 0 ? !!answers.situation
    : step === 1 ? answers.needs.length > 0
    : step === 2 ? !!answers.timeline
    : answers.name.trim().length > 0 && answers.contact.trim().length > 0;

  const go = (d) => {
    setDir(d);
    setStep((s) => s + d);
  };

  const submit = async () => {
    if (!canNext) return;
    setStatus("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...answers, lang }),
      });
      if (!res.ok) throw new Error("send failed");
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  const waText = encodeURIComponent(
    `Hi Marina! I'm ${answers.name}. Situation: ${answers.situation}. Needs: ${answers.needs.join(", ")}. Timeline: ${answers.timeline}. ${answers.message}`
  );

  const optionCls = (active) =>
    `text-left border rounded-2xl px-6 py-4 w-full transition-colors ${
      active
        ? "bg-terracotta-soft border-bronze/60"
        : "bg-white border-stone hover:border-bronze/50"
    }`;

  const inputCls =
    "w-full bg-transparent border-b border-ink/25 focus:border-bronze outline-none py-3 text-ink placeholder:text-stone-fg transition-colors";

  const OptionCard = ({ opt, active, testid, onClick }) => (
    <motion.button
      whileTap={{ scale: 0.96 }}
      whileHover={{ y: -3 }}
      transition={{ type: "spring", stiffness: 400, damping: 22 }}
      onClick={onClick}
      data-testid={testid}
      className={optionCls(active)}
    >
      <span className="flex items-start justify-between gap-3">
        <span>
          <span className={`block ${active ? "font-semibold text-ink" : "text-ink"}`}>{opt.label}</span>
          {opt.sub && <span className="block text-sm text-stone-fg mt-1">{opt.sub}</span>}
        </span>
        <AnimatePresence>
          {active && (
            <motion.span
              initial={{ scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0 }}
              className="bg-olive text-white rounded-full p-1 shrink-0 mt-0.5"
            >
              <Check size={14} aria-hidden />
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </motion.button>
  );

  if (status === "sent") {
    return (
      <div className="text-center py-10" data-testid="quiz-success">
        <motion.div
          initial={{ scale: 0, rotate: -12 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 15 }}
        >
          <PartyPopper size={52} className="mx-auto text-bronze mb-6" aria-hidden />
        </motion.div>
        <h3 className="font-serif text-3xl md:text-5xl text-ink mb-4">{q.successTitle}</h3>
        <p className="text-ink/70 mb-8 max-w-md mx-auto">{q.successBody}</p>
        <a
          href={`${CONTACT.whatsapp}?text=${waText}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="quiz-success-whatsapp"
          className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-ink font-medium px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
        >
          <MessageCircle size={17} aria-hidden /> {q.successCta}
        </a>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12" data-testid="lead-quiz">
      <div className="lg:col-span-4">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-olive mb-4">{q.overline}</p>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink mb-5" data-testid="quiz-title">{q.title}</h2>
        <p className="text-ink/70 mb-8">{q.sub}</p>
        <div className="flex lg:flex-col gap-6">
          <div className="flex gap-2">
            {STEP_ICONS.map((Icon, i) => (
              <span
                key={i}
                className={`p-2.5 rounded-full border transition-all ${
                  i === step
                    ? "bg-terracotta border-terracotta text-ink scale-110"
                    : i < step
                    ? "bg-olive border-olive text-white"
                    : "border-stone text-stone-fg"
                }`}
                data-testid={`quiz-step-icon-${i}`}
              >
                <Icon size={16} aria-hidden />
              </span>
            ))}
          </div>
          <div>
            <div className="h-1.5 bg-stone rounded-full overflow-hidden w-40">
              <motion.div
                className="h-full bg-terracotta"
                animate={{ width: `${((step + 1) / 4) * 100}%` }}
                transition={{ type: "spring", stiffness: 120, damping: 20 }}
              />
            </div>
            <p className="mt-2.5 font-mono text-xs text-stone-fg" data-testid="quiz-progress">
              {step + 1} {q.stepLabel}
            </p>
            <AnimatePresence mode="wait">
              {q.cheer[step] && (
                <motion.p
                  key={step}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mt-2 font-serif italic text-bronze"
                  data-testid="quiz-cheer"
                >
                  {q.cheer[step]}
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="lg:col-span-8 bg-white border border-stone rounded-2xl p-6 md:p-12 min-h-[320px] flex flex-col">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 36 * dir }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -36 * dir }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="flex-1"
            data-testid={`quiz-step-${step}`}
          >
            {step === 0 && (
              <div>
                <p className="font-serif text-2xl md:text-3xl text-ink mb-7">{q.s1q}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {q.s1.map((opt, i) => (
                    <OptionCard key={i} opt={opt} active={answers.situation === opt.label}
                      testid={`quiz-situation-${i}`}
                      onClick={() => setAnswers((a) => ({ ...a, situation: opt.label }))} />
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <p className="font-serif text-2xl md:text-3xl text-ink mb-7">{q.s2q}</p>
                <div className="flex flex-wrap gap-3">
                  {q.s2.map((opt, i) => {
                    const active = answers.needs.includes(opt);
                    return (
                      <motion.button
                        key={i}
                        whileTap={{ scale: 0.93 }}
                        whileHover={{ y: -2 }}
                        transition={{ type: "spring", stiffness: 400, damping: 20 }}
                        data-testid={`quiz-need-${i}`}
                        onClick={() => toggleNeed(opt)}
                        className={`border rounded-full px-5 py-2.5 text-sm transition-colors inline-flex items-center gap-2 ${
                          active
                            ? "bg-azul text-white border-azul font-medium"
                            : "bg-white border-stone text-ink hover:border-azul"
                        }`}
                      >
                        {active && <Check size={14} aria-hidden />}
                        {opt}
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <p className="font-serif text-2xl md:text-3xl text-ink mb-7">{q.s3q}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {q.s3.map((opt, i) => (
                    <OptionCard key={i} opt={opt} active={answers.timeline === opt.label}
                      testid={`quiz-timeline-${i}`}
                      onClick={() => setAnswers((a) => ({ ...a, timeline: opt.label }))} />
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-7">
                <p className="font-serif text-2xl md:text-3xl text-ink">{q.s4q}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                  <div>
                    <label htmlFor="quiz-name" className="block font-mono text-xs uppercase tracking-[0.2em] text-stone-fg mb-1">{q.name}</label>
                    <input id="quiz-name" data-testid="quiz-name-input" type="text" value={answers.name}
                      onChange={(e) => setAnswers((a) => ({ ...a, name: e.target.value }))} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="quiz-contact" className="block font-mono text-xs uppercase tracking-[0.2em] text-stone-fg mb-1">{q.contact}</label>
                    <input id="quiz-contact" data-testid="quiz-contact-input" type="text" value={answers.contact}
                      onChange={(e) => setAnswers((a) => ({ ...a, contact: e.target.value }))} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label htmlFor="quiz-message" className="block font-mono text-xs uppercase tracking-[0.2em] text-stone-fg mb-1">{q.message}</label>
                  <textarea id="quiz-message" data-testid="quiz-message-input" rows={3} value={answers.message}
                    onChange={(e) => setAnswers((a) => ({ ...a, message: e.target.value }))} className={`${inputCls} resize-none`} />
                </div>
                {status === "error" && (
                  <p className="text-bronze font-medium" data-testid="quiz-error">
                    {q.errSend}{" "}
                    <a href={`${CONTACT.whatsapp}?text=${waText}`} target="_blank" rel="noopener noreferrer" className="underline">WhatsApp</a>
                  </p>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-10 pt-6 border-t border-stone">
          <motion.button
            onClick={() => go(-1)}
            disabled={step === 0}
            whileTap={{ scale: 0.95 }}
            data-testid="quiz-back-button"
            className="inline-flex items-center gap-2 text-ink/60 hover:text-ink disabled:opacity-0 transition-all font-medium"
          >
            <ArrowLeft size={17} aria-hidden /> {q.back}
          </motion.button>
          {step < 3 ? (
            <motion.button
              onClick={() => canNext && go(1)}
              disabled={!canNext}
              whileTap={{ scale: 0.95 }}
              data-testid="quiz-next-button"
              className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-ink font-medium px-6 py-3 rounded-full transition-colors disabled:opacity-40"
            >
              {q.next} <ArrowRight size={17} aria-hidden />
            </motion.button>
          ) : (
            <motion.button
              onClick={submit}
              disabled={!canNext || status === "sending"}
              whileTap={{ scale: 0.95 }}
              data-testid="quiz-submit-button"
              className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-ink font-medium px-6 py-3 rounded-full transition-colors disabled:opacity-40"
            >
              <Send size={16} aria-hidden /> {status === "sending" ? "…" : q.submit}
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadQuiz;
